"use client";

import { useScriptStore } from "@/hooks/use-script";
import { createScriptSchema } from "@/lib/validations/script";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as z from "zod";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputTags } from "@/components/script/input-tags";
import { Combobox } from "@/components/ui/combo-box";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import { useRouter } from "next/navigation";
import type { APIResponse, Script } from "@/types/script";
import { useTransition } from "react";
import { useDropzone } from "react-dropzone";
import { useCallback } from "react";
import { Upload, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "@/lib/utils";
import { Switch } from "../ui/switch";
import MDEditor from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import { GameSelection } from "@/components/game/game-selection";

interface Game {
  universeId: number;
  name: string;
  thumbnail: string;
  creator: string;
  playerCount?: number;
}

const MAX_FILES = 3;
const MAX_FILE_SIZE = 1024 * 1024 * 2;

interface EditScriptFormProps {
  script: Script;
}

export default function EditScriptForm({ script }: EditScriptFormProps) {
  const { updateScript, script: scriptStore } = useScriptStore();
  const { toast } = useToast();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { accessToken } = useAuth();

  const form = useForm<z.infer<typeof createScriptSchema>>({
    resolver: zodResolver(createScriptSchema),
    defaultValues: {
      title: script.title,
      description: script.description,
      images: script.images.map((image) => image.path),
      tags: scriptStore.tags,
      games: [],
      mobile: false,
      key_system: false,
      payment: "Free",
    },
  });

  const payment = form.watch("payment");
  const isFree = payment === "Free";

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const currentCount = form.getValues("images").length;
      const remainingSlots = MAX_FILES - currentCount;

      if (remainingSlots <= 0) {
        toast({
          title: "Error",
          description: `Maximum ${MAX_FILES} images allowed`,
          variant: "destructive",
        });
        return;
      }

      const validFiles = acceptedFiles
        .slice(0, remainingSlots)
        .filter((file) => {
          if (file.size > MAX_FILE_SIZE) {
            toast({
              title: "Error",
              description: `${file.name} is too large. Max size is 2MB`,
              variant: "destructive",
            });
            return false;
          }
          return true;
        });

      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          form.setValue("images", [...form.getValues("images"), base64]);
        };
        reader.readAsDataURL(file);
      });
    },
    [form, toast],
  );

  const handleImageDelete = useCallback(
    (index: number) => {
      const currentImages = form.getValues("images");
      const newImages = [...currentImages];
      newImages.splice(index, 1);
      form.setValue("images", newImages);
    },
    [form],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp", ".avif"],
    },
    maxFiles: MAX_FILES,
    maxSize: MAX_FILE_SIZE,
    onDrop,
    disabled: form.getValues("images").length >= MAX_FILES,
  });

  async function onSubmit(data: z.infer<typeof createScriptSchema>) {
    startTransition(async () => {
      try {
        const body: any = {
          title: data.title,
          description: data.description,
          images: data.images,
          tags: script.tags,
          source: data.source,
          mobile: data.mobile,
          key_system: data.key_system,
          payment: data.payment,
        };

        if (data.games && data.games.length > 0) {
          body.games = data.games.map((game) => game.universeId.toString());
        }

        const response = await fetch(
          `${env.NEXT_PUBLIC_API_URL}/post/${script.slug}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(body),
          },
        );

        const responseData: APIResponse = await response.json();

        if (responseData.message !== "OK") {
          if (responseData.errors) {
            Object.keys(responseData.errors).forEach((key) => {
              const error = responseData.errors![key];
              toast({
                title: "Error",
                description: `${error!.param.path}: ${error!.param.msg}`,
                variant: "destructive",
              });
            });
          } else {
            toast({
              title: "Error",
              description: responseData.message,
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Success",
            description: "Script updated successfully",
          });
          router.push(`/script/${responseData.data?.post.slug}`);
        }
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  }

  return (
    <ScrollArea className="h-[750px] h-full md:h-auto">
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="grid md:grid-cols-5 md:space-x-6">
            <ScrollArea className="md:col-span-3 md:max-h-[500px]">
              <h2 className="mb-3 font-medium">Basic Information</h2>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Script Title*</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter title of the script"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="mt-2">
                    <FormLabel>Description*</FormLabel>
                    <FormControl>
                      <div className="w-full">
                        <MDEditor
                          value={field.value}
                          onChange={field.onChange}
                          preview="edit"
                          textareaProps={{
                            maxLength: 20000,
                          }}
                          previewOptions={{
                            rehypePlugins: [[rehypeSanitize]],
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="images"
                render={({ field }) => (
                  <FormItem className="mt-2">
                    <FormLabel>Images*</FormLabel>
                    <FormControl>
                      <div>
                        <div
                          {...getRootProps({
                            className:
                              "hover:bg-white/5 transition hover:cursor-pointer gap-2 flex items-center justify-center flex-col border-dashed border border-input py-8 px-4 rounded-md",
                          })}
                        >
                          <input {...getInputProps()} />
                          <div className="rounded-full bg-white/5 p-4">
                            <Upload
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          </div>
                          <span className="flex flex-col gap-px text-center">
                            <p>Drag and drop your images here</p>
                            <span className="text-xs text-muted-foreground">
                              You can upload 3 images (up to 2MB each)
                            </span>
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {field.value.map((image, index) => (
                            <div key={index} className="group relative">
                              <img
                                src={`https://cdn.robloxscripts.gg/public/${image}`}
                                alt={`Uploaded image ${index + 1}`}
                                className="h-20 w-20 rounded-md object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleImageDelete(index)}
                                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
                              >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Remove image</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="games"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Games</FormLabel>
                    <FormControl>
                      <GameSelection
                        selectedGames={field.value || []}
                        onChange={(games) => {
                          field.onChange(games);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </ScrollArea>

            <div className="mt-4 space-y-2 md:col-span-2 md:mt-0">
              <h2 className="mb-3 font-medium">Additional Information</h2>
              <Controller
                name="tags"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags*</FormLabel>
                    <InputTags
                      value={scriptStore.tags ?? []}
                      onChange={(tags) =>
                        updateScript({
                          tags: tags as string[],
                        })
                      }
                      placeholder="Enter values, comma separated..."
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-input p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Mobile Compatible
                      </FormLabel>
                      <FormDescription>
                        Is your script mobile compatible?
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="key_system"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      "flex flex-row items-center justify-between rounded-lg border border-input p-4",
                      !isFree && "opacity-50",
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Key System</FormLabel>
                      <FormDescription>
                        Does your script have a key system?
                        {!isFree && " (Only available for free scripts)"}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        disabled={!isFree}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="payment"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-input p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Payment</FormLabel>
                      <FormDescription>
                        Is your script free or paid?
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Combobox
                        {...field}
                        options={[
                          { value: "Free", label: "Free" },
                          { value: "Paid", label: "Paid" },
                        ]}
                        selected={field.value}
                        placeholder="Select option..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="w-full">
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </Form>
    </ScrollArea>
  );
}
